// Non-blocking "new version available" prompt for the stale-bundle check
// (see @/lib/appVersion). Shows when the server's build id differs from this
// bundle's. Dismissible, but RECURRING: every fresh mismatch report (next API
// response or next focus-poll) re-shows it, so a tester can't run stale forever
// after one dismiss. The hard-error forced reload lives in queryClient.ts; this
// is the gentle path.
import { useEffect, useState } from "react";
import { X } from "lucide-react";
import {
  CLIENT_BUILD_ID,
  dismissUpdate,
  isUpdateAvailable,
  onUpdateAvailable,
  reportServerBuildId,
} from "@/lib/appVersion";
import { PRODUCT_NAME } from "@shared/brand";

export function UpdateAvailableBanner() {
  const [show, setShow] = useState(isUpdateAvailable());

  // Re-show on every mismatch report (recurring after dismiss).
  useEffect(() => onUpdateAvailable(() => setShow(true)), []);

  // Focus-poll: when the tab becomes visible, ask the server its version and
  // compare — catches a new deploy before the user even hits an API error.
  useEffect(() => {
    if (!CLIENT_BUILD_ID) return; // dev build without a stamped version → nothing to compare
    const check = async () => {
      try {
        const res = await fetch("/api/version", { cache: "no-store" });
        if (!res.ok) return;
        const data = await res.json();
        reportServerBuildId(data?.version);
      } catch {
        /* offline / transient → ignore */
      }
    };
    const onVisible = () => {
      if (document.visibilityState === "visible") check();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, []);

  if (!show) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="update-available-banner"
      className="fixed bottom-4 left-1/2 z-[100] flex -translate-x-1/2 items-center gap-3 rounded-lg border border-border bg-popover px-4 py-2.5 text-sm text-popover-foreground shadow-lg"
    >
      <span>A new version of {PRODUCT_NAME} is available.</span>
      <button
        onClick={() => window.location.reload()}
        data-testid="button-reload-app"
        className="rounded-md bg-primary px-3 py-1 text-xs font-medium text-primary-foreground hover:opacity-90"
      >
        Reload
      </button>
      <button
        onClick={() => { dismissUpdate(); setShow(false); }}
        aria-label="Dismiss"
        data-testid="button-dismiss-update"
        className="text-muted-foreground hover:text-foreground"
      >
        <X className="h-4 w-4" aria-hidden="true" />
      </button>
    </div>
  );
}
